import { useState, type Dispatch, type SetStateAction } from 'react'
import { arrayMove } from '@dnd-kit/sortable'
import { PAGE_BLOCK_REGISTRY } from '../page-blocks/registry'
import type { PageBlockType } from '../../api/types'
import type { EditorialGridLayoutPreset } from '../page-blocks/definitions/editorialGrid'
import type { CanvasSlot, ColBlock, ColKey, PickerMode } from './canvasTypes'

export interface BlockCanvasState {
  slots: CanvasSlot[]
  selectedId: string | null
  selectedColumnType: PageBlockType | null
  selectedColKey: ColKey | null
  selectedColBlockId: string | null
  pickerMode: PickerMode
  selectedSlot: CanvasSlot | null
  editorialLeadTypes: PageBlockType[]
  editorialRowTypes: PageBlockType[]
  structuralTypes: PageBlockType[]
  columnCompatibleTypes: PageBlockType[]
  setSlots: Dispatch<SetStateAction<CanvasSlot[]>>
  setSelectedId: (id: string | null) => void
  setSelectedColumnType: (type: PageBlockType | null) => void
  setSelectedColKey: (key: ColKey | null) => void
  setSelectedColBlockId: (id: string | null) => void
  setPickerMode: (mode: PickerMode) => void
  addBlock: (type: PageBlockType) => void
  addEditorialGrid: (preset: EditorialGridLayoutPreset) => void
  assignColumnBlock: (slotId: string, colKey: ColKey, blockType: PageBlockType) => void
  clearColumnBlock: (slotId: string, colKey: ColKey) => void
  addColBlock: (slotId: string, colKey: ColKey, blockType: PageBlockType) => void
  removeColBlock: (slotId: string, colKey: ColKey, blockId: string) => void
  moveColBlock: (slotId: string, colKey: ColKey, blockId: string, direction: 'up' | 'down') => void
  removeBlock: (id: string) => void
  moveBlock: (id: string, direction: 'up' | 'down') => void
  reorderBlocks: (fromIndex: number, toIndex: number) => void
  updateSlotConfig: (id: string, key: string, value: string | number | boolean | null) => void
  // Provisional slot lifecycle — used by shelf drag-to-canvas
  insertSlot: (slot: CanvasSlot) => void
  removeSlotById: (id: string) => void
}

export function useBlockCanvas(onMutate?: () => void): BlockCanvasState {
  const [slots, setSlots] = useState<CanvasSlot[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedColumnType, setSelectedColumnType] = useState<PageBlockType | null>(null)
  const [selectedColKey, setSelectedColKey] = useState<ColKey | null>(null)
  const [selectedColBlockId, setSelectedColBlockId] = useState<string | null>(null)
  const [pickerMode, setPickerMode] = useState<PickerMode>({ kind: 'add' })

  const editorialLeadTypes: PageBlockType[] = ['inline-feature', 'feature-with-rail', 'hero-overlay']
  const editorialRowTypes: PageBlockType[] = ['editorial-grid', 'story-row', 'livewire-callout']
  const structuralTypes = (Object.keys(PAGE_BLOCK_REGISTRY) as PageBlockType[]).filter(
    (t) => PAGE_BLOCK_REGISTRY[t].rowCompatible && PAGE_BLOCK_REGISTRY[t].category === 'structural',
  )
  const columnCompatibleTypes = (Object.keys(PAGE_BLOCK_REGISTRY) as PageBlockType[]).filter(
    (t) => PAGE_BLOCK_REGISTRY[t].columnCompatible,
  )

  const selectedSlot = slots.find((s) => s.id === selectedId) ?? null

  function addBlock(type: PageBlockType) {
    if (type === 'editorial-grid') return // editorial-grid must go through addEditorialGrid
    const newSlot: CanvasSlot = {
      id: crypto.randomUUID(),
      type,
      order: slots.length,
      config: { ...PAGE_BLOCK_REGISTRY[type].defaultConfig },
    }
    setSlots((prev) => [...prev, newSlot])
    setSelectedId(newSlot.id)
    onMutate?.()
  }

  function addEditorialGrid(preset: EditorialGridLayoutPreset) {
    const newSlot: CanvasSlot = {
      id: crypto.randomUUID(),
      type: 'editorial-grid',
      order: slots.length,
      config: { layoutPreset: preset, column1: null, column2: null, column3: null },
      columnBlocks: {},
    }
    setSlots((prev) => [...prev, newSlot])
    setSelectedId(newSlot.id)
    onMutate?.()
  }

  function assignColumnBlock(slotId: string, colKey: ColKey, blockType: PageBlockType) {
    setSlots((prev) =>
      prev.map((s) => (s.id === slotId ? { ...s, config: { ...s.config, [colKey]: blockType } } : s)),
    )
    setPickerMode({ kind: 'add' })
    onMutate?.()
  }

  function clearColumnBlock(slotId: string, colKey: ColKey) {
    setSlots((prev) =>
      prev.map((s) => (s.id === slotId ? { ...s, config: { ...s.config, [colKey]: null } } : s)),
    )
    onMutate?.()
  }

  function addColBlock(slotId: string, colKey: ColKey, blockType: PageBlockType) {
    const newBlock: ColBlock = { id: crypto.randomUUID(), type: blockType }
    setSlots((prev) =>
      prev.map((s) => {
        if (s.id !== slotId) return s
        const existing = s.columnBlocks?.[colKey] ?? []
        return { ...s, columnBlocks: { ...s.columnBlocks, [colKey]: [...existing, newBlock] } }
      }),
    )
    setPickerMode({ kind: 'add' })
    onMutate?.()
  }

  function removeColBlock(slotId: string, colKey: ColKey, blockId: string) {
    setSlots((prev) =>
      prev.map((s) => {
        if (s.id !== slotId) return s
        const filtered = (s.columnBlocks?.[colKey] ?? []).filter((b) => b.id !== blockId)
        return { ...s, columnBlocks: { ...s.columnBlocks, [colKey]: filtered } }
      }),
    )
    onMutate?.()
  }

  function moveColBlock(slotId: string, colKey: ColKey, blockId: string, direction: 'up' | 'down') {
    setSlots((prev) =>
      prev.map((s) => {
        if (s.id !== slotId) return s
        const arr = [...(s.columnBlocks?.[colKey] ?? [])]
        const idx = arr.findIndex((b) => b.id === blockId)
        if (idx === -1) return s
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1
        if (swapIdx < 0 || swapIdx >= arr.length) return s
        ;[arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]]
        return { ...s, columnBlocks: { ...s.columnBlocks, [colKey]: arr } }
      }),
    )
    onMutate?.()
  }

  function removeBlock(id: string) {
    setSlots((prev) => prev.filter((s) => s.id !== id).map((s, i) => ({ ...s, order: i })))
    if (selectedId === id) {
      const remaining = slots.filter((s) => s.id !== id)
      setSelectedId(remaining.length > 0 ? remaining[remaining.length - 1].id : null)
      setSelectedColumnType(null)
      setSelectedColKey(null)
    }
    if (pickerMode.kind === 'assign' && pickerMode.slotId === id) {
      setPickerMode({ kind: 'add' })
    }
    onMutate?.()
  }

  function moveBlock(id: string, direction: 'up' | 'down') {
    setSlots((prev) => {
      const idx = prev.findIndex((s) => s.id === id)
      if (idx === -1) return prev
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1
      if (swapIdx < 0 || swapIdx >= prev.length) return prev
      const next = [...prev]
      ;[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]]
      return next.map((s, i) => ({ ...s, order: i }))
    })
    onMutate?.()
  }

  function reorderBlocks(fromIndex: number, toIndex: number) {
    setSlots((prev) => arrayMove(prev, fromIndex, toIndex).map((s, i) => ({ ...s, order: i })))
    onMutate?.()
  }

  function updateSlotConfig(id: string, key: string, value: string | number | boolean | null) {
    setSlots((prev) =>
      prev.map((s) => (s.id === id ? { ...s, config: { ...s.config, [key]: value } } : s)),
    )
    onMutate?.()
  }

  // ── Provisional slot lifecycle ─────────────────────────────────────────────
  // Used by BlockEditorDndContext to insert a real slot the moment a shelf drag
  // starts. This lets SortableContext animate the item during drag, and the
  // existing reorder logic positions it correctly on drop.

  function insertSlot(slot: CanvasSlot) {
    setSlots((prev) => [...prev, slot])
    onMutate?.()
  }

  function removeSlotById(id: string) {
    setSlots((prev) => prev.filter((s) => s.id !== id))
    onMutate?.()
  }

  return {
    slots,
    selectedId,
    selectedColumnType,
    selectedColKey,
    selectedColBlockId,
    pickerMode,
    selectedSlot,
    editorialLeadTypes,
    editorialRowTypes,
    structuralTypes,
    columnCompatibleTypes,
    setSlots,
    setSelectedId,
    setSelectedColumnType,
    setSelectedColKey,
    setSelectedColBlockId,
    setPickerMode,
    addBlock,
    addEditorialGrid,
    assignColumnBlock,
    clearColumnBlock,
    addColBlock,
    removeColBlock,
    moveColBlock,
    removeBlock,
    moveBlock,
    reorderBlocks,
    updateSlotConfig,
    insertSlot,
    removeSlotById,
  }
}
